import React from "react";
import {
  ThemeProvider,
  createTheme,
  CssBaseline,
  useMediaQuery,
  Theme,
} from "@mui/material";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import LandingPage from "./components/LandingPage";
import { useFirebaseAuth } from "./hooks/useFirebaseAuth";
import BusDetails from "./components/BusDetails";
import CreateTrip from "./components/CreateTrip";
import TripDetails from "./pages/TripDetails";
import Trip from "./pages/Trip";
import Layout from "./components/Layout";
import Vehicle from "./pages/vehicle/Vehicle";
import AddVehicle from "./pages/vehicle/AddVehicle";
import Dashboard from "./pages/Dashboard";
import SelectVehicle from "./pages/trip/SelectVehicle";
import { SnackbarProvider } from "notistack";
import VehicleDetails from "./pages/vehicle/VehicleDetails";
import Staff from "./pages/Staff";
import AddStaff from "./components/AddStaff";
import NotRegistered from "./components/NotRegistered";
import { Loading } from "./components/Loading";
import ServiceOperators from "./pages/masterData/serviceOperator/ServiceOperators";
import AddServiceOperator from "./pages/masterData/serviceOperator/AddServiceOperator";
import ComingSoon from "./components/ComingSoon";
import CompanyProfile from "./pages/companyProfile/CompanyProfile";
import MasterData from "./pages/masterData/MasterData";
import VehicleExpense from "./pages/vehicleExpense/vehicleExpense";
import DriverCleaner from "./pages/driverCleaner/driverCleaner";
import StaffForm from "./pages/driverCleaner/StaffForm";
import AddBasicTripDetails from "./pages/trip/add-trip/AddBasicTripDetails";
import AddExpenseDetails from "./pages/trip/add-trip/AddExpenseDetails";
import Mechanics from "./pages/masterData/mechanic/Mechanics";
import AddProof from "./pages/trip/add-trip/AddProof";
import PetrolPumps from "./pages/masterData/petrolPump/PetrolPumps";
import VehicleFinance from "./pages/vehicle/VehicleFinance";
import Parking from "./pages/masterData/parking/Parking";
import Garage from "./pages/masterData/garage/Garage";
import VehicleDocument from "./pages/vehicle/VehicleDocument";
import VehicleInsurance from "./pages/vehicle/VehicleInsurance";
import VehiclePermit from "./pages/vehicle/VehiclePermit";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import TabComponent from "./pages/companyProfile/tabComponents/TabComponent";
import AddExpense from "./pages/vehicleExpense/addExpense";
import EditExpense from "./pages/vehicleExpense/editExpense";
import Parties from "./pages/masterData/party/Parties";
import Clients from "./pages/masterData/client/Clients";
import ReportPage from "./pages/report/report";
import ReportByTypePage from "./pages/report/reportByType";
import PrivacyPolicy from "./pages/policies/privacyPolicy";
import TermsOfConditions from "./pages/termsOfCondition/termsOfConditions";
import SecurityPolicy from "./pages/securityPolicy/securityPolicy";
import RentalBooking from "./pages/rentalBooking/RentalBooking";
import NewBooking from "./pages/rentalBooking/NewBooking";
import DailyLogs from "./pages/rentalBooking/logs/DailyLogs";
import VehicleMaintenance from "./pages/vehicle/vehicleService/VehicleMaintenance";
import AddServiceLog from "./pages/vehicle/vehicleService/components/AddServiceLog";
import MarketLoad from "./pages/masterData/marketLoad/MarketLoad";
import TripForm from "./pages/trip/dailyBusTrip/tripForm";
import SessionWrapper from "./components/SessionWrapper";
import RouteComponent from "./pages/masterData/route/RouteComponent";
import OtherExpense from "./pages/otherExpense/OtherExpense";
import AddOtherExpense from "./pages/otherExpense/AddExpense";
import EditOtherExpense from "./pages/otherExpense/EditExpense";
import FixedIncome from "./pages/fixedIncome/FixedIncome";
import FixedIncomeForm from "./pages/fixedIncome/FixedIncomeForm";
import FixedIncomeDailyLogs from "./pages/fixedIncome/FixedIncomeDailyLogs";
import PaymentStatus from "./pages/payments/PaymentStatus";
import AccountLocked from "./pages/AccountLocked";
import Branch from "./pages/masterData/branch/Branch";
import PickupMan from "./pages/masterData/pickupMan/PickupMan";
import ProjectModules from "./project/project_module/pages/ProjectModules";
import ProjectDetailView from "./project/project_module/pages/ProjectDetailView";
import CreateAllocation from "./project/project_module/pages/CreateAllocation";
import ProjectAddExpense from "./project/project_module/pages/ProjectAddExpense";
import ProjectEditExpense from "./project/project_module/pages/ProjectEditExpense";
import { PROJECT_TERMINOLOGY } from "./project/project_module/enums";
import VehicleOdometerReadings from "./pages/vehicle/VehicleOdometerReadings";
import ProjectLogs from "./project/project_module/pages/ProjectLogs";
import AddProjectLog from "./project/project_module/pages/AddProjectLog";
import EditProjectLog from "./project/project_module/pages/EditProjectLog";
import AddRouteProject from "./project/project_module/pages/master/AddRouteProject";
import ContractForm from "./project/project_module/pages/ContractForm";
import { ProjectProvider } from "./project/project_module/context/ProjectContext";
import AddRoute from "./pages/masterData/route/AddRoute";
import FuelCard from "./pages/masterData/fuelCard/FuelCard";
import SuperProject from "./project/project_module/pages/SuperProject";
import SuperProjectForm from "./project/project_module/pages/SuperProjectForm";
import SuperProjectView from "./project/project_module/pages/SuperProjectView";
import ProjectLogsByVehicle from "./project/project_module/pages/ProjectLogsByVehicle";
import AddProjectLogByVehicle from "./project/project_module/pages/AddProjectLogByVehicle";
import Transactions from "./pages/finance/Transactions";
import IncomeTransactions from "./pages/finance/IncomeTransaction";
import Finance from "./pages/finance/Finance";
import PartyDetails from "./pages/finance/PartyDetails";
import IncomePartyDetails from "./pages/finance/IncomePartyDetails";
import StaffAttendance from "./pages/masterData/StaffAttendance";
import Inventory from "./pages/masterData/inventory/Inventory";
import InventoryCategory from "./pages/masterData/inventory/InventoryCategory";
import FuelReportsRouter from "./pages/fuel-reports/FuelReportsRouter";
import JobReportsPage from "./pages/job-reports/JobReportsPage";
import RMReportsPage from "./pages/rm-reports/RMReportsPage";
import Chat from "./pages/chat/Chat";

// Enhanced theme with custom breakpoints and screen size utilities
const theme = createTheme({
  breakpoints: {
    values: {
      xs: 0, // Mobile portrait
      sm: 600, // Mobile landscape / small tablet
      md: 900, // Tablet
      lg: 1200, // Small desktop
      xl: 1536, // Large desktop
    },
  },
  palette: {
    primary: {
      main: "#2b69e8", // Simple blue that's easy on eyes
      light: "#64b5f6",
      dark: "#1976d2",
    },
    secondary: {
      main: "#4caf50", // Simple green for success states
      light: "#81c784",
      dark: "#388e3c",
    },
    background: {
      default: "whitesmoke", // Light blue background
      paper: "#ffffff",
    },
    text: {
      primary: "#333333",
      secondary: "#666666",
    },
  },
  typography: {
    fontFamily: '"Inter", "Segoe UI", "Roboto", "Arial", sans-serif',
    h3: {
      fontWeight: 600,
      fontSize: "clamp(1.2rem, 4vw, 1.75rem)", // Responsive font size
    },
    h4: {
      fontWeight: 600,
      fontSize: "clamp(1.1rem, 3.5vw, 1.5rem)", // Responsive font size
    },
    h6: {
      fontWeight: 600,
      fontSize: "clamp(0.9rem, 2.5vw, 1.25rem)", // For metric card values
    },
    body1: {
      fontSize: "clamp(0.875rem, 2vw, 1rem)",
    },
    body2: {
      fontSize: "clamp(0.75rem, 1.8vw, 0.875rem)",
    },
    subtitle1: {
      fontWeight: 500,
      fontSize: "clamp(0.8rem, 2.2vw, 1rem)", // For metric card titles
    },
    caption: {
      fontSize: "clamp(0.65rem, 1.5vw, 0.75rem)", // For metric card data items
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          textTransform: "none",
          padding: "12px 24px",
          fontSize: "1rem",
          [theme.breakpoints.down("sm")]: {
            padding: "8px 16px",
            fontSize: "0.875rem",
          },
        }),
        containedPrimary: {
          boxShadow: "none",
          "&:hover": {
            boxShadow: "none",
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            borderRadius: 4,
          },
          // Prevent mouse wheel from changing number input values
          "& input[type='number']": {
            "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
              WebkitAppearance: "none",
              margin: 0,
            },
            "&[type=number]": {
              MozAppearance: "textfield",
            },
          },
        },
      },
      // Add default props to prevent wheel events on number inputs
      defaultProps: {
        InputProps: {
          inputProps: {
            onWheel: (e: React.WheelEvent) => e.preventDefault(),
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
          borderRadius: 4,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: ({ theme }) => ({
          transition: "all 0.2s ease-in-out",
          "&:hover": {
            transform: "translateY(-1px)",
            boxShadow: "0 4px 8px rgba(0,0,0,0.15)",
          },
          [theme.breakpoints.down("sm")]: {
            borderRadius: 8,
          },
        }),
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: ({ theme }) => ({
          [theme.breakpoints.down("sm")]: {
            padding: theme.spacing(1.5),
            "&:last-child": {
              paddingBottom: theme.spacing(1.5),
            },
          },
          [theme.breakpoints.between("sm", "lg")]: {
            padding: theme.spacing(1.5),
            "&:last-child": {
              paddingBottom: theme.spacing(1.5),
            },
          },
        }),
      },
    },
    MuiContainer: {
      styleOverrides: {
        root: ({ theme }) => ({
          [theme.breakpoints.down("sm")]: {
            paddingLeft: theme.spacing(1),
            paddingRight: theme.spacing(1),
          },
        }),
      },
    },
    MuiGrid: {
      styleOverrides: {
        container: ({ theme }) => ({
          [theme.breakpoints.down("sm")]: {
            margin: 0,
            width: "100%",
            "& > .MuiGrid-item": {
              paddingLeft: theme.spacing(1),
              paddingTop: theme.spacing(1),
            },
          },
        }),
      },
    },
  },
});

// Enhanced screen size utilities and custom hooks
export const useResponsiveBreakpoints = () => {
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const isSmallDesktop = useMediaQuery(theme.breakpoints.down("lg"));
  const isLargeDesktop = useMediaQuery(theme.breakpoints.up("xl"));

  // Enhanced screen detection
  const isMobilePortrait = useMediaQuery("(max-width: 480px)");
  const isMobileLandscape = useMediaQuery(
    "(min-width: 481px) and (max-width: 767px)"
  );
  const isTabletPortrait = useMediaQuery(
    "(min-width: 768px) and (max-width: 1023px)"
  );
  const isDesktop = useMediaQuery("(min-width: 1024px)");

  // Screen density detection
  const isHighDensity = useMediaQuery(
    "(-webkit-min-device-pixel-ratio: 2), (min-resolution: 192dpi)"
  );

  // Utility functions
  const getScreenCategory = () => {
    if (isMobilePortrait) return "mobile-portrait";
    if (isMobileLandscape) return "mobile-landscape";
    if (isTabletPortrait) return "tablet";
    if (isSmallDesktop) return "small-desktop";
    if (isLargeDesktop) return "large-desktop";
    return "desktop";
  };

  const getGridColumns = () => {
    if (isMobile) return 1;
    if (isTablet) return 2;
    if (isSmallDesktop) return 4;
    return 5; // For dashboard 5-column layout
  };

  const getMetricCardSize = () => ({
    titleSize: isMobile ? "0.8rem" : isSmallDesktop ? "0.85rem" : "0.95rem",
    valueSize: isMobile ? "1rem" : isSmallDesktop ? "0.9rem" : "1.2rem",
    subtitleSize: isMobile ? "0.7rem" : isSmallDesktop ? "0.65rem" : "0.75rem",
    dataSize: isMobile ? "0.65rem" : isSmallDesktop ? "0.66rem" : "0.72rem",
    padding: isMobile ? 1.5 : isSmallDesktop ? 1.5 : 2,
    gap: isMobile ? 1.5 : isSmallDesktop ? 0.5 : 1.5,
  });

  return {
    // Basic breakpoints
    isMobile,
    isTablet,
    isSmallDesktop,
    isLargeDesktop,

    // Enhanced detection
    isMobilePortrait,
    isMobileLandscape,
    isTabletPortrait,
    isDesktop,
    isHighDensity,

    // Utility functions
    getScreenCategory,
    getGridColumns,
    getMetricCardSize,

    // Theme-aware spacing
    spacing: {
      xs: theme.spacing(0.5),
      sm: theme.spacing(1),
      md: theme.spacing(2),
      lg: theme.spacing(3),
      xl: theme.spacing(4),
    },
  };
};

// Protected Route component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user, loading } = useFirebaseAuth();

  if (loading) {
    return <Loading message="Loading..." fullScreen={true} />;
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

// Public Route component
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useFirebaseAuth();

  if (loading) {
    return <Loading message="Loading..." fullScreen={true} />;
  }

  // If user is authenticated redirect to dashboard
  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  // Allow access to public routes
  return <>{children}</>;
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false, // disable automatic refetching on window focus
      retry: 1, // only retry failed requests once
    },
  },
});

const App: React.FC = () => {
  // Add useEffect to disable right-click and developer tools
  React.useEffect(() => {
    // Force hide reCAPTCHA badge on app load and periodically
    const hideRecaptchaBadge = () => {
      try {
        // Hide reCAPTCHA badge
        const badges = document.querySelectorAll(".grecaptcha-badge");
        badges.forEach((badge) => {
          (badge as HTMLElement).style.display = "none";
          (badge as HTMLElement).style.visibility = "hidden";
        });

        // Hide any reCAPTCHA iframes
        const iframes = document.querySelectorAll(
          'iframe[src*="recaptcha"], iframe[src*="google.com/recaptcha"]'
        );
        iframes.forEach((iframe) => {
          (iframe as HTMLElement).style.display = "none";
        });

        // Hide any elements with data-sitekey
        const sitekeyElements = document.querySelectorAll("div[data-sitekey]");
        sitekeyElements.forEach((element) => {
          (element as HTMLElement).style.display = "none";
        });
      } catch (error) {
        // Ignore errors in hiding elements
      }
    };

    // Hide immediately
    hideRecaptchaBadge();

    // Set up periodic hiding (every 5 seconds) to catch any dynamically added badges
    const hideInterval = setInterval(hideRecaptchaBadge, 5000);

    // Disable right-click context menu
    const handleContextMenu = (e: MouseEvent) => {
      e.preventDefault();
      return false;
    };

    // Disable F12, Ctrl+Shift+I, Ctrl+Shift+J, Ctrl+U, Ctrl+Shift+C
    const handleKeyDown = (e: KeyboardEvent) => {
      // Disable F12
      if (e.key === "F12") {
        e.preventDefault();
        return false;
      }

      // Disable Ctrl+Shift+I (Inspector)
      if (e.ctrlKey && e.shiftKey && e.key === "I") {
        e.preventDefault();
        return false;
      }

      // Disable Ctrl+Shift+J (Console)
      if (e.ctrlKey && e.shiftKey && e.key === "J") {
        e.preventDefault();
        return false;
      }

      // Disable Ctrl+U (View Source)
      if (e.ctrlKey && e.key === "u") {
        e.preventDefault();
        return false;
      }

      // Disable Ctrl+Shift+C (Inspect Element)
      if (e.ctrlKey && e.shiftKey && e.key === "C") {
        e.preventDefault();
        return false;
      }

      // Disable Ctrl+S (Save)
      if (e.ctrlKey && e.key === "s") {
        e.preventDefault();
        return false;
      }
    };

    // Add event listeners
    document.addEventListener("contextmenu", handleContextMenu);
    document.addEventListener("keydown", handleKeyDown);

    // Cleanup function to remove event listeners
    return () => {
      document.removeEventListener("contextmenu", handleContextMenu);
      document.removeEventListener("keydown", handleKeyDown);
      clearInterval(hideInterval);
    };
  }, []);

  return (
    <HelmetProvider>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <SnackbarProvider maxSnack={3}>
          <QueryClientProvider client={queryClient}>
            <SessionWrapper>
              <Router>
                <Routes>
                  <Route
                    path="/"
                    element={
                      <PublicRoute>
                        <LandingPage />
                      </PublicRoute>
                    }
                  />
                  <Route
                    path={`/super`}
                    element={
                      <ProtectedRoute>
                        <Layout sidebarKey={PROJECT_TERMINOLOGY.LOWER_PLURAL}>
                          <SuperProject />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path={`/super/:id`}
                    element={
                      <ProtectedRoute>
                        <Layout sidebarKey={PROJECT_TERMINOLOGY.LOWER_PLURAL}>
                          <SuperProjectView />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path={`/super/form/:id?`}
                    element={
                      <ProtectedRoute>
                        <Layout sidebarKey={PROJECT_TERMINOLOGY.LOWER_PLURAL}>
                          <SuperProjectForm />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path={`/${PROJECT_TERMINOLOGY.LOWER_PLURAL}`}
                    element={
                      <ProtectedRoute>
                        <Layout sidebarKey={PROJECT_TERMINOLOGY.LOWER_PLURAL}>
                          <ProjectModules />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path={`/${PROJECT_TERMINOLOGY.LOWER_PLURAL}/:id`}
                    element={
                      <ProtectedRoute>
                        <Layout sidebarKey={PROJECT_TERMINOLOGY.LOWER_PLURAL}>
                          <ProjectProvider>
                            <ProjectDetailView />
                          </ProjectProvider>
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path={`/super/:superProjectId/${PROJECT_TERMINOLOGY.LOWER_PLURAL}/:id`}
                    element={
                      <ProtectedRoute>
                        <Layout sidebarKey={PROJECT_TERMINOLOGY.LOWER_PLURAL}>
                          <ProjectProvider>
                            <ProjectDetailView />
                          </ProjectProvider>
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path={`/${PROJECT_TERMINOLOGY.LOWER_PLURAL}/:projectId/allocations/new`}
                    element={
                      <ProtectedRoute>
                        <Layout sidebarKey={PROJECT_TERMINOLOGY.LOWER_PLURAL}>
                          <ProjectProvider>
                            <CreateAllocation />
                          </ProjectProvider>
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path={`/super/:superProjectId/${PROJECT_TERMINOLOGY.LOWER_PLURAL}/:projectId/allocations/new`}
                    element={
                      <ProtectedRoute>
                        <Layout sidebarKey={PROJECT_TERMINOLOGY.LOWER_PLURAL}>
                          <ProjectProvider>
                            <CreateAllocation />
                          </ProjectProvider>
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/project-expense/add/:projectId"
                    element={
                      <ProtectedRoute>
                        <Layout sidebarKey={PROJECT_TERMINOLOGY.LOWER_PLURAL}>
                          <ProjectProvider>
                            <ProjectAddExpense />
                          </ProjectProvider>
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/super/:superProjectId/project-expense/add/:projectId"
                    element={
                      <ProtectedRoute>
                        <Layout sidebarKey={PROJECT_TERMINOLOGY.LOWER_PLURAL}>
                          <ProjectProvider>
                            <ProjectAddExpense />
                          </ProjectProvider>
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/project-expense/edit/:projectId/:expenseId"
                    element={
                      <ProtectedRoute>
                        <Layout sidebarKey={PROJECT_TERMINOLOGY.LOWER_PLURAL}>
                          <ProjectProvider>
                            <ProjectEditExpense />
                          </ProjectProvider>
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/super/:superProjectId/project-expense/edit/:projectId/:expenseId"
                    element={
                      <ProtectedRoute>
                        <Layout sidebarKey={PROJECT_TERMINOLOGY.LOWER_PLURAL}>
                          <ProjectProvider>
                            <ProjectEditExpense />
                          </ProjectProvider>
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path={`/${PROJECT_TERMINOLOGY.LOWER_PLURAL}/:projectId/contracts/add`}
                    element={
                      <ProtectedRoute>
                        <Layout sidebarKey={PROJECT_TERMINOLOGY.LOWER_PLURAL}>
                          <ProjectProvider>
                            <ContractForm />
                          </ProjectProvider>
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path={`/${PROJECT_TERMINOLOGY.LOWER_PLURAL}/:projectId/contracts/:contractId/edit`}
                    element={
                      <ProtectedRoute>
                        <Layout sidebarKey={PROJECT_TERMINOLOGY.LOWER_PLURAL}>
                          <ProjectProvider>
                            <ContractForm />
                          </ProjectProvider>
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path={`/${PROJECT_TERMINOLOGY.LOWER_PLURAL}/:id/logs/new`}
                    element={
                      <ProtectedRoute>
                        <Layout sidebarKey={PROJECT_TERMINOLOGY.LOWER_PLURAL}>
                          <AddProjectLog />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path={`/super/:superProjectId/${PROJECT_TERMINOLOGY.LOWER_PLURAL}/:id/logs/new`}
                    element={
                      <ProtectedRoute>
                        <Layout sidebarKey={PROJECT_TERMINOLOGY.LOWER_PLURAL}>
                          <AddProjectLog />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path={`/${PROJECT_TERMINOLOGY.LOWER_PLURAL}/:id/logs/new/by-vehicle`}
                    element={
                      <ProtectedRoute>
                        <Layout sidebarKey={PROJECT_TERMINOLOGY.LOWER_PLURAL}>
                          <AddProjectLogByVehicle />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path={`/super/:superProjectId/${PROJECT_TERMINOLOGY.LOWER_PLURAL}/:id/logs/new/by-vehicle`}
                    element={
                      <ProtectedRoute>
                        <Layout sidebarKey={PROJECT_TERMINOLOGY.LOWER_PLURAL}>
                          <AddProjectLogByVehicle />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path={`/super/:superProjectId/${PROJECT_TERMINOLOGY.LOWER_PLURAL}/:id/logs/edit`}
                    element={
                      <ProtectedRoute>
                        <Layout sidebarKey={PROJECT_TERMINOLOGY.LOWER_PLURAL}>
                          <EditProjectLog />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path={`/${PROJECT_TERMINOLOGY.LOWER_PLURAL}/:id/logs/edit`}
                    element={
                      <ProtectedRoute>
                        <Layout sidebarKey={PROJECT_TERMINOLOGY.LOWER_PLURAL}>
                          <EditProjectLog />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path={`/${PROJECT_TERMINOLOGY.LOWER_PLURAL}/:id/logs`}
                    element={
                      <ProtectedRoute>
                        <Layout sidebarKey={PROJECT_TERMINOLOGY.LOWER_PLURAL}>
                          <ProjectLogs />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path={`/super/:superProjectId/${PROJECT_TERMINOLOGY.LOWER_PLURAL}/:id/logs`}
                    element={
                      <ProtectedRoute>
                        <Layout sidebarKey={PROJECT_TERMINOLOGY.LOWER_PLURAL}>
                          <ProjectLogs />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path={`/${PROJECT_TERMINOLOGY.LOWER_PLURAL}/:id/logs/by-vehicle`}
                    element={
                      <ProtectedRoute>
                        <Layout sidebarKey={PROJECT_TERMINOLOGY.LOWER_PLURAL}>
                          <ProjectLogsByVehicle />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path={`/super/:superProjectId/${PROJECT_TERMINOLOGY.LOWER_PLURAL}/:id/logs/by-vehicle`}
                    element={
                      <ProtectedRoute>
                        <Layout sidebarKey={PROJECT_TERMINOLOGY.LOWER_PLURAL}>
                          <ProjectLogsByVehicle />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path={`/${PROJECT_TERMINOLOGY.LOWER_PLURAL}/:id/route/add`}
                    element={
                      <ProtectedRoute>
                        <Layout sidebarKey={PROJECT_TERMINOLOGY.LOWER_PLURAL}>
                          <AddRouteProject />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path={`/super/:superProjectId/${PROJECT_TERMINOLOGY.LOWER_PLURAL}/:id/route/add`}
                    element={
                      <ProtectedRoute>
                        <Layout sidebarKey={PROJECT_TERMINOLOGY.LOWER_PLURAL}>
                          <AddRouteProject />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                  <Route
                    path="/terms-of-conditions"
                    element={<TermsOfConditions />}
                  />
                  <Route path="/security-policy" element={<SecurityPolicy />} />
                  <Route
                    path="/dashboard"
                    element={
                      <ProtectedRoute>
                        <Layout sidebarKey="dashboard">
                          <Dashboard />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/account-locked"
                    element={
                      <ProtectedRoute>
                        <AccountLocked />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/payments/status/:orderId"
                    element={
                      <ProtectedRoute>
                        <PaymentStatus />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/trips"
                    element={
                      <ProtectedRoute>
                        <Layout sidebarKey="trips">
                          <Trip />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/trip/select-vehicle"
                    element={
                      <ProtectedRoute>
                        <Layout sidebarKey="trips">
                          <SelectVehicle />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/trip/basic/:vehicleId/:tripId?"
                    element={
                      <ProtectedRoute>
                        <Layout sidebarKey="trips">
                          <AddBasicTripDetails />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/trip/expense/:vehicleId?/:tripId?"
                    element={
                      <ProtectedRoute>
                        <Layout sidebarKey="trips">
                          <AddExpenseDetails />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/trip/proof/:tripId?"
                    element={
                      <ProtectedRoute>
                        <Layout sidebarKey="trips">
                          <AddProof />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  {/* Daily Bus Trip Routes */}
                  <Route
                    path="/trip/daily-bus/:section"
                    element={
                      <ProtectedRoute>
                        <Layout sidebarKey="trips">
                          <TripForm />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/trip/daily-bus/:tripId/:section"
                    element={
                      <ProtectedRoute>
                        <Layout sidebarKey="trips">
                          <TripForm />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  {/* Daily Bus Trip Routes */}
                  <Route
                    path="/trip/daily-bus"
                    element={
                      <ProtectedRoute>
                        <Layout sidebarKey="trips">
                          <TripForm />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/trip/daily-bus/:section"
                    element={
                      <ProtectedRoute>
                        <Layout sidebarKey="trips">
                          <TripForm />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/trip/daily-bus/:tripId/:section"
                    element={
                      <ProtectedRoute>
                        <Layout sidebarKey="trips">
                          <TripForm />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/fuel"
                    element={
                      <ProtectedRoute>
                        <Layout sidebarKey="fuel">
                          <ComingSoon />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/staff"
                    element={
                      <ProtectedRoute>
                        <Layout sidebarKey="staff">
                          <Staff />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/staff/add/:type"
                    element={
                      <ProtectedRoute>
                        <Layout sidebarKey="staff">
                          <AddStaff />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/finance/:financeType?"
                    element={
                      <ProtectedRoute>
                        <Layout sidebarKey="finance">
                          <Finance />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/finance/transactions/:partyType/:partyId"
                    element={
                      <ProtectedRoute>
                        <Layout sidebarKey="finance">
                          <Transactions />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/finance/transactions/income/:partyId"
                    element={
                      <ProtectedRoute>
                        <Layout sidebarKey="finance">
                          <IncomeTransactions />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/finance/expense/party/:partyType"
                    element={
                      <ProtectedRoute>
                        <Layout sidebarKey="finance">
                          <PartyDetails />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/finance/income/party/:partyType"
                    element={
                      <ProtectedRoute>
                        <Layout sidebarKey="finance">
                          <IncomePartyDetails />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/vehicles/add"
                    element={
                      <ProtectedRoute>
                        <Layout sidebarKey="profile">
                          <AddVehicle />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/vehicles/:vehicleId/edit"
                    element={
                      <ProtectedRoute>
                        <Layout sidebarKey="profile">
                          <AddVehicle />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/vehicles/:vehicleId/finance"
                    element={
                      <ProtectedRoute>
                        <Layout sidebarKey="profile">
                          <VehicleFinance />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/vehicles/:vehicleId/maintenance/:model"
                    element={
                      <ProtectedRoute>
                        <Layout sidebarKey="profile">
                          <VehicleMaintenance />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/vehicles/:vehicleId/maintenance/:model/add"
                    element={
                      <ProtectedRoute>
                        <Layout sidebarKey="profile">
                          <AddServiceLog />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/vehicles/:vehicleId/maintenance/:model/edit/:logId"
                    element={
                      <ProtectedRoute>
                        <Layout sidebarKey="profile">
                          <AddServiceLog />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/vehicles/:vehicleId/documents"
                    element={
                      <ProtectedRoute>
                        <Layout sidebarKey="profile">
                          <VehicleDocument />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/vehicles/:vehicleId/insurances"
                    element={
                      <ProtectedRoute>
                        <Layout sidebarKey="profile">
                          <VehicleInsurance />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/vehicles/:vehicleId/permits"
                    element={
                      <ProtectedRoute>
                        <Layout sidebarKey="profile">
                          <VehiclePermit />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/vehicles/:vehicleId"
                    element={
                      <ProtectedRoute>
                        <Layout sidebarKey="profile">
                          <VehicleDetails />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/vehicles/:vehicleId/odometer-readings"
                    element={
                      <ProtectedRoute>
                        <Layout sidebarKey="profile">
                          <VehicleOdometerReadings />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/maintenance"
                    element={
                      <ProtectedRoute>
                        <Layout sidebarKey="maintenance">
                          <ComingSoon />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/reports"
                    element={
                      <ProtectedRoute>
                        <Layout sidebarKey="reports">
                          <ReportPage />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/reports/:type"
                    element={
                      <ProtectedRoute>
                        <Layout sidebarKey="reports">
                          <ReportByTypePage />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/fuel-reports/:type?"
                    element={
                      <ProtectedRoute>
                        <Layout sidebarKey="fuel-reports">
                          <FuelReportsRouter />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/r-m-reports"
                    element={
                      <ProtectedRoute>
                        <Layout sidebarKey="r-m-reports">
                          <RMReportsPage />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/job-reports"
                    element={
                      <ProtectedRoute>
                        <Layout sidebarKey="job-reports">
                          <JobReportsPage />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route path="/not-registered" element={<NotRegistered />} />
                  <Route
                    path="/trip/:tripId"
                    element={
                      <ProtectedRoute>
                        <Layout sidebarKey="trips">
                          <TripDetails />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/master-data/operators"
                    element={
                      <ProtectedRoute>
                        <Layout sidebarKey="profile">
                          <ServiceOperators />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/master-data/mechanics"
                    element={
                      <ProtectedRoute>
                        <Layout sidebarKey="profile">
                          <Mechanics />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/master-data/petrol-pumps"
                    element={
                      <ProtectedRoute>
                        <Layout sidebarKey="profile">
                          <PetrolPumps />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/master-data/garages"
                    element={
                      <ProtectedRoute>
                        <Layout sidebarKey="profile">
                          <Garage />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/master-data/parkings"
                    element={
                      <ProtectedRoute>
                        <Layout sidebarKey="profile">
                          <Parking />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  {/* <Route
                path="/master-data"
                element={
                  <ProtectedRoute>
                    <Layout sidebarKey="company-profile">
                      <MasterData />
                    </Layout>
                  </ProtectedRoute>
                }
              /> */}
                  <Route
                    path="/master-data/operators/:operatorId?"
                    element={
                      <ProtectedRoute>
                        <Layout sidebarKey="profile">
                          <AddServiceOperator />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/:tab"
                    element={
                      <ProtectedRoute>
                        <Layout sidebarKey="profile">
                          <TabComponent />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/vehicle-expense"
                    element={
                      <ProtectedRoute>
                        <Layout sidebarKey="vehicle-expense">
                          <VehicleExpense />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/vehicle-expense/add"
                    element={
                      <ProtectedRoute>
                        <Layout sidebarKey="vehicle-expense">
                          <AddExpense />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/vehicle-expense/edit/:expenseId"
                    element={
                      <ProtectedRoute>
                        <Layout sidebarKey="vehicle-expense">
                          <EditExpense />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  {/* Driver Cleaner */}
                  <Route
                    path="/master-data/staff/:type"
                    element={
                      <ProtectedRoute>
                        <Layout sidebarKey="profile">
                          <DriverCleaner />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/master-data/staff/:type/new"
                    element={
                      <ProtectedRoute>
                        <Layout sidebarKey="profile">
                          <StaffForm />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/master-data/staff/:type/:id"
                    element={
                      <ProtectedRoute>
                        <Layout sidebarKey="profile">
                          <StaffForm />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/master-data/staff-attendance"
                    element={
                      <ProtectedRoute>
                        <Layout sidebarKey="profile">
                          <StaffAttendance />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/master-data/parties"
                    element={
                      <ProtectedRoute>
                        <Layout sidebarKey="profile">
                          <Parties />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/master-data/clients"
                    element={
                      <ProtectedRoute>
                        <Layout sidebarKey="profile">
                          <Clients />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />

                  {/* Booking */}
                  <Route
                    path="/rental"
                    element={
                      <ProtectedRoute>
                        <Layout sidebarKey="booking">
                          <RentalBooking />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/rental/:id"
                    element={
                      <ProtectedRoute>
                        <Layout sidebarKey="booking">
                          <NewBooking />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/rental/:rentalJourneyId/logs"
                    element={
                      <ProtectedRoute>
                        <Layout sidebarKey="booking">
                          <DailyLogs />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />

                  {/* Master Data Market Load */}
                  <Route
                    path="/master-data/market-load"
                    element={
                      <ProtectedRoute>
                        <Layout sidebarKey="profile">
                          <MarketLoad />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />

                  {/* Master Data Routes */}
                  <Route
                    path="/master-data/routes"
                    element={
                      <ProtectedRoute>
                        <Layout sidebarKey="profile">
                          <RouteComponent />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/master-data/routes/add"
                    element={
                      <ProtectedRoute>
                        <Layout sidebarKey="profile">
                          <AddRoute />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/master-data/routes/:routeId/edit"
                    element={
                      <ProtectedRoute>
                        <Layout sidebarKey="profile">
                          <AddRoute />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/master-data/branch"
                    element={
                      <ProtectedRoute>
                        <Layout sidebarKey="profile">
                          <Branch />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/master-data/pickup-man"
                    element={
                      <ProtectedRoute>
                        <Layout sidebarKey="profile">
                          <PickupMan />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/other-expense"
                    element={
                      <ProtectedRoute>
                        <Layout sidebarKey="other-expense">
                          <OtherExpense />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/other-expense/add/:date?"
                    element={
                      <ProtectedRoute>
                        <Layout sidebarKey="other-expense">
                          <AddOtherExpense />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/other-expense/edit/:expenseId"
                    element={
                      <ProtectedRoute>
                        <Layout sidebarKey="other-expense">
                          <EditOtherExpense />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />

                  {/* Fixed Income */}
                  <Route
                    path="/fixed-income"
                    element={
                      <ProtectedRoute>
                        <Layout sidebarKey="fixed-income">
                          <FixedIncome />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/fixed-income/:section/:id?"
                    element={
                      <ProtectedRoute>
                        <Layout sidebarKey="fixed-income">
                          <FixedIncomeForm />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/fixed-income/:tripId/:section"
                    element={
                      <ProtectedRoute>
                        <Layout sidebarKey="fixed-income">
                          <FixedIncomeForm />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/fixed-income/:id/logs"
                    element={
                      <ProtectedRoute>
                        <Layout sidebarKey="fixed-income">
                          <FixedIncomeDailyLogs />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />

                  {/* Fuel card master */}
                  <Route
                    path="/master-data/fuel-card"
                    element={
                      <ProtectedRoute>
                        <Layout sidebarKey="profile">
                          <FuelCard />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />

                  {/* Inventory */}
                  <Route
                    path="/master-data/inventory"
                    element={
                      <ProtectedRoute>
                        <Layout sidebarKey="profile">
                          <Inventory />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/master-data/inventory/:category/:categoryId"
                    element={
                      <ProtectedRoute>
                        <Layout sidebarKey="profile">
                          <InventoryCategory />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/chat"
                    element={
                      <ProtectedRoute>
                        <Layout sidebarKey="dashboard">
                          <Chat />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                </Routes>
              </Router>
            </SessionWrapper>
          </QueryClientProvider>
        </SnackbarProvider>
      </ThemeProvider>
    </HelmetProvider>
  );
};

export default App;
